import React, { useEffect, useState } from "react";
import CanvasPageRenderer from "./CanvasPageRenderer";
import { FieldRenderer, getFieldValueForUser } from "../fields/FieldRenderer";
import { A4_WIDTH, A4_HEIGHT } from "../constants/layoutConstants";
import Button from "../../Buttons/Button";

export const DocumentPreview = ({
  document,
  mode = "preview",
  userId,
  userRole, //initiator,applicant,approver
  selectedApplicant,
  onSelectApplicant,
  applicants = [],
  onBack,
  onEdit,
  onUpdateField,
  onDownload,
  onSubmit,
  currentPage: externalCurrentPage,
  onPageChange,
}) => {
  const [currentPage, setCurrentPage] = useState(externalCurrentPage || 1);
  const [validationErrors, setValidationErrors] = useState([]);
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    if (
      externalCurrentPage !== undefined &&
      externalCurrentPage !== currentPage
    ) {
      setCurrentPage(externalCurrentPage);
    }
  }, [externalCurrentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const getFieldRenderMode = (field) => {
    switch (mode) {
      case "preview":
        return { editable: false, visible: true, role: field.role };

      case "initiator":
        return {
          editable: field.role === "initiator",
          visible: field.role === "initiator",
          role: "initiator",
        };

      case "applicant":
        return {
          editable: field.role === "applicant",
          visible: field.role === "initiator" || field.role === "applicant",
          role: field.role,
        };

      case "approver":
        return {
          editable: field.role === "approver",
          visible: true,
          role: field.role,
        };

      default:
        return { editable: false, visible: true, role: field.role };
    }
  };

  // Get field value based on mode and role - NEW IMPLEMENTATION
  const getFieldValue = (field) => {
    // In approver mode, show appropriate user's data
    if (mode === "approver") {
      if (field.role === "initiator") {
        // Get initiator's value from field structure
        const initiatorId = getInitiatorId();
        return getFieldValueForUser(field, initiatorId);
      }
      if (field.role === "applicant") {
        // Get selected applicant's value
        return getFieldValueForUser(field, selectedApplicant);
      }
      if (field.role === "approver") {
        // Get current approver's value
        return getFieldValueForUser(field, userId);
      }
    }
    // In applicant mode
    else if (mode === "applicant") {
      if (field.role === "initiator") {
        const initiatorId = getInitiatorId();
        return getFieldValueForUser(field, initiatorId);
      }
      if (field.role === "applicant") {
        return getFieldValueForUser(field, userId);
      }
    }
    // In initiator mode
    else if (mode === "initiator") {
      return getFieldValueForUser(field, userId);
    }

    // Preview mode - show first available value or default
    if (
      field.value &&
      typeof field.value === "object" &&
      field.value.type === "userFilled"
    ) {
      const users = Object.keys(field.value.data || {});
      if (users.length > 0) {
        return field.value.data[users[0]].value;
      }
    }
    return field.value || "";
  };

  // Get initiator ID from workflow
  const getInitiatorId = () => {
    if (!document) return null;
    const initiators = document.workflows
      ?.map((w) => {
        if (Array.isArray(w.initiator) && w.initiator.length > 0) {
          return String(w.initiator[0]).trim();
        }
        if (w.initiator?.value) {
          return String(w.initiator.value).trim();
        }
        return null;
      })
      .filter(Boolean);
    return initiators?.length > 0 ? initiators[0] : null;
  };

  // Validate required fields
  const validateFields = () => {
    const errors = [];
    const roleToCheck =
      mode === "approver"
        ? "approver"
        : mode === "applicant"
        ? "applicant"
        : "initiator";

    Object.values(document.droppedFields || {})
      .flat()
      .forEach((field) => {
        if (field.role === roleToCheck && field.required) {
          const value = getFieldValue(field);
          if (!value || String(value).trim() === "") {
            errors.push({
              fieldId: field.id,
              label: field.label || "Unnamed Field",
              page: field.page,
            });
          }
        }
      });

    return errors;
  };

  // Update the setCurrentPage calls to notify parent

  const renderHeader = () => {
    const titles = {
      preview: "Document Preview",
      initiator: `Initiator - Fill Form (${userId})`,
      applicant: `Applicant - Fill Form (${userId})`,
      approver: `Approver Review - ${userId}`,
    };

    return (
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            type="icon"
            icon={"chevron_left"}
            onClick={onBack}
            color="black"
            variant="text"
          />
          <div>
            <h2 className="text-xl font-semibold">{document.documentName}</h2>
            <p className="text-sm text-gray-500">{titles[mode]}</p>
          </div>
        </div>

        <div className="flex gap-3">
          {mode === "preview" && (
            <Button
              type="icon"
              icon={"edit"}
              color="black"
              variant="text"
              onClick={onEdit}
            />
          )}

          {mode === "approver" && (
            <>
              <Button title={"download"} onClick={onDownload} />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderFields = () => {
    const pageFields = document.droppedFields?.[currentPage] || [];

    return pageFields.map((field) => {
      const renderMode = getFieldRenderMode(field);
      if (!renderMode.visible) return null;

      const value = getFieldValue(field) || "";

      const hasError = validationErrors.some((e) => e.fieldId === field.id);

      return (
        <div
          key={field.id}
          style={{
            position: "absolute",
            left: `${field.x}px`,
            top: `${field.y}px`,
            zIndex: renderMode.editable ? 100 : 10,
            pointerEvents: "auto",
          }}
          className={hasError ? "ring-2 ring-red-500 rounded" : ""}
        >
          <FieldRenderer
            field={field}
            value={value}
            userId={userId}
            userRole={userRole}
            onChange={(val) => {
              if (renderMode.editable && onUpdateField) {
                // onUpdateField now stores directly in field structure
                onUpdateField(field.id, val, userId, document);
              }
              setValidationErrors((prev) =>
                prev.filter((e) => e.fieldId !== field.id)
              );
            }}
            readOnly={!renderMode.editable}
            role={renderMode.role}
            mode="fill"
          />
        </div>
      );
    });
  };

  return (
    <div className="flex flex-1 bg-gray-100">
      <div className="flex-1 flex flex-col">
        {mode === "preview" && renderHeader()}

        {validationErrors.length > 0 && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-start gap-2">
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">
                Please fill all required fields
              </p>
              <ul className="mt-1 text-xs text-red-700">
                {validationErrors.slice(0, 3).map((error, idx) => (
                  <li key={idx}>
                    {error.label} - Page {error.page}
                  </li>
                ))}
                {validationErrors.length > 3 && (
                  <li>... and {validationErrors.length - 3} more</li>
                )}
              </ul>
            </div>
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 overflow-auto p-8 flex justify-center">
            <div className="max-w-4xl w-full">
              <div className="flex items-center justify-between mb-4">
                <Button
                  type="icon"
                  icon={"chevron_left"}
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  variant="text"
                  color="black"
                />
                <span className="text-sm font-medium">
                  Page {currentPage} of {document.pageCount || 1}
                </span>
                <Button
                  type="icon"
                  icon={"chevron_right"}
                  onClick={() =>
                    handlePageChange(
                      Math.min(document.pageCount || 1, currentPage + 1)
                    )
                  }
                  disabled={currentPage === (document.pageCount || 1)}
                  variant="text"
                  color="black"
                />
              </div>

              <div
                className="relative bg-white shadow-lg mx-auto"
                style={{
                  width: `${A4_WIDTH}px`,
                  height: `${A4_HEIGHT}px`,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                >
                  <CanvasPageRenderer
                    arrayBuffer={document.arrayBuffer}
                    pageNumber={currentPage}
                    width={A4_WIDTH}
                    height={A4_HEIGHT}
                    isBlankDocument={document.isBlankDocument}
                  />
                </div>

                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    pointerEvents: "none",
                  }}
                >
                  {renderFields()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
